import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Paper, Alert, CircularProgress,
  Fade, TextField, IconButton, Tooltip, Grid,
} from '@mui/material';
import {
  ShieldCheck, Calendar, Clock, Copy, Check, CheckCircle2,
  Send, Monitor, ArrowLeft, QrCode,
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

const SectionCard = styled(Paper)(() => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  padding: 24,
  marginBottom: 24,
}));

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const toLocalISO = (d) => d.toLocaleDateString('en-CA'); // YYYY-MM-DD

// Próximos N dias de atendimento (seg-sáb).
const buildOpenDays = (count = 14) => {
  const days = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  while (days.length < count) {
    const wd = d.getDay();
    if (wd >= 1 && wd <= 6) {
      days.push({
        iso: toLocalISO(d),
        wd: WEEKDAYS[wd],
        day: d.getDate(),
        month: MONTHS[d.getMonth()],
      });
    }
    d.setDate(d.getDate() + 1);
  }
  return days;
};

export default function AgendarVerificacao() {
  const [price, setPrice] = useState(300);
  const [openDays] = useState(buildOpenDays(14));
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [method, setMethod] = useState('invite');
  const [observacao, setObservacao] = useState('');

  const [booking, setBooking] = useState(false);
  const [order, setOrder] = useState(null); // { order_id }
  const [qr, setQr] = useState(null);
  const [paid, setPaid] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/bm-booking/config`, authHeader())
      .then(r => { if (r.data?.success) setPrice(r.data.price); })
      .catch(() => {});
  }, [authHeader]);

  const loadAvailability = useCallback(async (dateIso) => {
    setSlotsLoading(true); setSelectedTime(null); setSlots([]);
    try {
      const r = await axios.get(`${API}/api/bm-booking/availability`, { params: { date: dateIso }, ...authHeader() });
      if (r.data?.success) setSlots(r.data.slots || []);
    } catch { setError('Erro ao carregar horários'); }
    finally { setSlotsLoading(false); }
  }, [authHeader]);

  const handleSelectDate = (iso) => {
    setSelectedDate(iso);
    setError('');
    loadAvailability(iso);
  };

  const handleBook = async () => {
    setError('');
    if (!selectedDate || !selectedTime) { setError('Selecione uma data e um horário.'); return; }
    setBooking(true);
    try {
      const r = await axios.post(`${API}/api/bm-booking/book`,
        { date: selectedDate, time: selectedTime, method, observacao },
        { ...authHeader(), timeout: 60000 });
      if (r.data?.success) {
        setOrder({ order_id: r.data.order_id });
        loadQr(r.data.order_id);
        startPolling(r.data.order_id);
      } else { setError(r.data?.message || 'Falha ao reservar'); }
    } catch (err) { setError(err.response?.data?.message || 'Erro ao criar a reserva'); }
    finally { setBooking(false); }
  };

  const loadQr = useCallback(async (orderId) => {
    try {
      const r = await axios.get(`${API}/api/pagamento/pix-qrcode/${orderId}`, authHeader());
      if (r.data?.success) setQr(r.data.qr);
    } catch { /* mostra link de fallback */ }
  }, [authHeader]);

  const startPolling = useCallback((orderId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/pagamento/status/${orderId}`, authHeader());
        if (r.data?.status === 'paid') {
          setPaid(true);
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch { /* continua tentando */ }
    }, 5000);
  }, [authHeader]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleCopy = () => {
    if (!qr?.payload) return;
    navigator.clipboard.writeText(qr.payload);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetAll = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setOrder(null); setQr(null); setPaid(false);
    setSelectedTime(null);
    if (selectedDate) loadAvailability(selectedDate);
  };

  // ===== Tela de sucesso =====
  if (paid) {
    return (
      <Fade in timeout={400}>
        <Box sx={{ maxWidth: 620, mx: 'auto' }}>
          <SectionCard elevation={0}>
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircle2 size={52} color="#10B981" style={{ marginBottom: 16 }} />
              <Typography variant="h5" sx={{ color: '#F9FAFB', fontWeight: 700, mb: 1 }}>Reserva confirmada!</Typography>
              <Typography sx={{ color: '#9CA3AF', mb: 3 }}>
                Seu horário <strong style={{ color: '#A78BFA' }}>{selectedTime}</strong> foi reservado com sucesso.
                Em breve entro em contato para conduzir a verificação da sua BM.
              </Typography>
              <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', textAlign: 'left', mb: 3 }}>
                <Typography sx={{ color: '#6EE7B7', fontWeight: 600, fontSize: '0.9rem', mb: 0.5 }}>
                  Método escolhido: {method === 'invite' ? 'Convite de acesso à BM' : 'Acesso remoto via AnyDesk'}
                </Typography>
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.82rem' }}>
                  {method === 'invite'
                    ? 'Você me enviará o convite de acesso à sua Business Manager no horário agendado.'
                    : 'No horário agendado, acessarei sua máquina em tempo real pelo AnyDesk para fazer a verificação.'}
                </Typography>
              </Box>
            </Box>
          </SectionCard>
        </Box>
      </Fade>
    );
  }

  // ===== Tela de pagamento (Pix) =====
  if (order) {
    return (
      <Fade in timeout={400}>
        <Box sx={{ maxWidth: 620, mx: 'auto' }}>
          <SectionCard elevation={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <QrCode size={20} color="#7C3AED" />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#F9FAFB', fontSize: '1rem' }}>
                Pague para confirmar a reserva
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 3 }}>
              {selectedDate?.split('-').reverse().join('/')} às <strong style={{ color: '#A78BFA' }}>{selectedTime}</strong> —
              o horário só fica reservado após o pagamento (R$ {price.toFixed(2).replace('.', ',')}).
            </Typography>

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              {qr ? (
                <>
                  {qr.encodedImage && (
                    <Box component="img" src={`data:image/png;base64,${qr.encodedImage}`} alt="QR Code Pix"
                      sx={{ width: 220, height: 220, borderRadius: 2, background: '#fff', p: 1, mb: 2 }} />
                  )}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1.5, borderRadius: 2, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <Typography sx={{ color: '#9CA3AF', fontSize: '0.72rem', flex: 1, wordBreak: 'break-all', textAlign: 'left' }}>
                      {qr.payload}
                    </Typography>
                    <Tooltip title={copied ? 'Copiado!' : 'Copiar código Pix'}>
                      <IconButton size="small" onClick={handleCopy}>
                        {copied ? <Check size={16} color="#10B981" /> : <Copy size={16} color="#6B7280" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </>
              ) : (
                <Box sx={{ py: 4 }}>
                  <CircularProgress size={30} sx={{ color: '#7C3AED' }} />
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem', mt: 1 }}>Gerando QR Code...</Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={14} sx={{ color: '#F59E0B' }} />
              <Typography sx={{ color: '#FCD34D', fontSize: '0.82rem' }}>Aguardando confirmação do pagamento...</Typography>
            </Box>

            <Button fullWidth variant="text" startIcon={<ArrowLeft size={16} />} onClick={resetAll}
              sx={{ textTransform: 'none', color: '#9CA3AF' }}>
              Escolher outro horário
            </Button>
          </SectionCard>
        </Box>
      </Fade>
    );
  }

  // ===== Tela principal (agenda) =====
  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 780, mx: 'auto' }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <ShieldCheck size={22} color="#7C3AED" />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#F9FAFB', letterSpacing: '-0.02em' }}>
              Agendar Verificação de BM
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Eu mesmo verifico sua Business Manager — atendimento direto e com garantia.
          </Typography>
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, background: 'rgba(239,68,68,0.1)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.2)', '& .MuiAlert-icon': { color: '#EF4444' } }}>{error}</Alert>}

        {/* Como funciona */}
        <SectionCard elevation={0}>
          <Typography sx={{ color: '#F9FAFB', fontWeight: 600, fontSize: '1rem', mb: 2 }}>Como funciona</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CheckCircle2 size={18} color="#10B981" />
              <Typography sx={{ color: '#D1D5DB', fontSize: '0.9rem' }}>
                Valor: <strong style={{ color: '#F9FAFB' }}>R$ {price.toFixed(2).replace('.', ',')}</strong> — pagamento via Pix (Asaas), com <strong style={{ color: '#6EE7B7' }}>garantia de 100%</strong> da verificação.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Clock size={18} color="#A78BFA" />
              <Typography sx={{ color: '#D1D5DB', fontSize: '0.9rem' }}>
                Atendimento de <strong style={{ color: '#F9FAFB' }}>segunda a sábado</strong>, das <strong style={{ color: '#F9FAFB' }}>09:00 às 17:30</strong>.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <ShieldCheck size={18} color="#A78BFA" style={{ marginTop: 2 }} />
              <Typography sx={{ color: '#D1D5DB', fontSize: '0.9rem' }}>
                A verificação pode ser feita de duas formas: você me envia o <strong style={{ color: '#F9FAFB' }}>acesso da BM por convite</strong>, ou eu faço pelo <strong style={{ color: '#F9FAFB' }}>AnyDesk</strong> (acesso à sua máquina em tempo real).
              </Typography>
            </Box>
          </Box>
        </SectionCard>

        {/* Data */}
        <SectionCard elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Calendar size={20} color="#7C3AED" />
            <Typography sx={{ color: '#F9FAFB', fontWeight: 600, fontSize: '1rem' }}>1. Escolha o dia</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
            {openDays.map(d => {
              const active = selectedDate === d.iso;
              return (
                <Box key={d.iso} onClick={() => handleSelectDate(d.iso)}
                  sx={{
                    cursor: 'pointer', flexShrink: 0, textAlign: 'center', px: 2, py: 1.5, borderRadius: 2, minWidth: 68,
                    background: active ? 'rgba(124,58,237,0.18)' : 'rgba(255,255,255,0.02)',
                    border: active ? '1px solid #7C3AED' : '1px solid rgba(255,255,255,0.08)',
                    transition: 'all 0.2s', '&:hover': { borderColor: '#7C3AED' },
                  }}>
                  <Typography sx={{ color: active ? '#A78BFA' : '#9CA3AF', fontSize: '0.72rem', fontWeight: 600 }}>{d.wd}</Typography>
                  <Typography sx={{ color: active ? '#F9FAFB' : '#D1D5DB', fontSize: '1.2rem', fontWeight: 700, lineHeight: 1.2 }}>{d.day}</Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.68rem' }}>{d.month}</Typography>
                </Box>
              );
            })}
          </Box>
        </SectionCard>

        {/* Horários */}
        {selectedDate && (
          <SectionCard elevation={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Clock size={20} color="#7C3AED" />
              <Typography sx={{ color: '#F9FAFB', fontWeight: 600, fontSize: '1rem' }}>2. Escolha o horário</Typography>
            </Box>
            {slotsLoading ? (
              <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={26} sx={{ color: '#7C3AED' }} /></Box>
            ) : slots.length === 0 ? (
              <Typography sx={{ color: '#6B7280', fontSize: '0.88rem' }}>Nenhum horário disponível nesse dia.</Typography>
            ) : (
              <Grid container spacing={1}>
                {slots.map(s => {
                  const active = selectedTime === s.time;
                  return (
                    <Grid item xs={3} sm={2} key={s.time}>
                      <Box onClick={() => s.available && setSelectedTime(s.time)}
                        sx={{
                          textAlign: 'center', py: 1, borderRadius: 2, fontWeight: 600, fontSize: '0.85rem',
                          cursor: s.available ? 'pointer' : 'not-allowed',
                          color: !s.available ? '#4B5563' : active ? '#fff' : '#D1D5DB',
                          background: !s.available ? 'rgba(255,255,255,0.02)' : active ? '#7C3AED' : 'rgba(124,58,237,0.08)',
                          border: `1px solid ${active ? '#7C3AED' : 'rgba(124,58,237,0.2)'}`,
                          textDecoration: s.available ? 'none' : 'line-through',
                          opacity: s.available ? 1 : 0.5,
                          transition: 'all 0.15s', '&:hover': s.available ? { background: active ? '#6D28D9' : 'rgba(124,58,237,0.18)' } : {},
                        }}>
                        {s.time}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </SectionCard>
        )}

        {/* Método */}
        {selectedDate && selectedTime && (
          <SectionCard elevation={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <ShieldCheck size={20} color="#7C3AED" />
              <Typography sx={{ color: '#F9FAFB', fontWeight: 600, fontSize: '1rem' }}>3. Método de verificação</Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
              {[
                { value: 'invite', label: 'Convite de acesso', desc: 'Você me envia o convite de acesso à sua BM.', icon: <Send size={18} /> },
                { value: 'anydesk', label: 'AnyDesk (remoto)', desc: 'Acesso à sua máquina em tempo real para eu verificar.', icon: <Monitor size={18} /> },
              ].map(opt => {
                const active = method === opt.value;
                return (
                  <Box key={opt.value} onClick={() => setMethod(opt.value)}
                    sx={{
                      cursor: 'pointer', p: 2, borderRadius: 2,
                      background: active ? 'rgba(124,58,237,0.18)' : 'rgba(255,255,255,0.02)',
                      border: active ? '1px solid #7C3AED' : '1px solid rgba(255,255,255,0.08)',
                      transition: 'all 0.2s', '&:hover': { borderColor: '#7C3AED' },
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, color: active ? '#A78BFA' : '#9CA3AF' }}>
                      {opt.icon}
                      <Typography sx={{ color: active ? '#F9FAFB' : '#D1D5DB', fontWeight: 600, fontSize: '0.9rem' }}>{opt.label}</Typography>
                    </Box>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.78rem' }}>{opt.desc}</Typography>
                  </Box>
                );
              })}
            </Box>
            <TextField fullWidth label="Observação (opcional)" value={observacao} onChange={e => setObservacao(e.target.value)}
              multiline minRows={2} placeholder="Algum detalhe que eu deva saber?" sx={{ mb: 3 }} />

            <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.2)', mb: 3 }}>
              <Typography sx={{ color: '#D1D5DB', fontSize: '0.9rem' }}>
                Resumo: <strong style={{ color: '#F9FAFB' }}>{selectedDate.split('-').reverse().join('/')}</strong> às <strong style={{ color: '#F9FAFB' }}>{selectedTime}</strong> —
                total <strong style={{ color: '#6EE7B7' }}>R$ {price.toFixed(2).replace('.', ',')}</strong>
              </Typography>
            </Box>

            <Button fullWidth variant="contained" size="large"
              startIcon={booking ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <QrCode size={20} />}
              disabled={booking} onClick={handleBook}
              sx={{ py: 1.5, borderRadius: 3, fontSize: '1rem', background: '#7C3AED', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#6D28D9' } }}>
              {booking ? 'Gerando pagamento...' : `Reservar e pagar R$ ${price.toFixed(2).replace('.', ',')}`}
            </Button>
          </SectionCard>
        )}
      </Box>
    </Fade>
  );
}
